import * as React from "react";

import { useAuth } from "@/providers";
import {
  EMPTY_ORG_DEFAULT_VIEWS,
  ORG_DEFAULT_VIEWS_KEY,
  type OrgDefaultViewsConfig,
} from "@/shared/domain/config/ConfigRecords";
import { useOrgConfig, useUpdateOrgConfig } from "@/shared/domain/config/hooks";
import type { QueryTarget } from "@/shared/domain/query";
import type { ViewRecord } from "@/shared/domain/views";

export interface OrgDefaultView {
  /** The View this org's members start this table from, or `null` for none. */
  orgDefaultViewId: string | null;
  /** Whether `view` is that default. */
  isOrgDefault: (view: ViewRecord) => boolean;
  /**
   * Make `view` the org's default for this table, replacing any existing one.
   * The server refuses unless the caller is an org admin and `view` is shared
   * org-wide.
   */
  setOrgDefault: (view: ViewRecord) => Promise<void>;
  /** Take the org default off this table. Org admins only. */
  clearOrgDefault: () => Promise<void>;
  /**
   * True until the stored setting has been read. Hold set and clear until
   * then: a write before that sends an empty map and drops the other tables'
   * defaults.
   */
  isPending: boolean;
  /** True while a set or clear is in flight. */
  isSaving: boolean;
}

/**
 * The View an org's members start one table from when they have chosen no
 * default of their own. Set and cleared by org admins.
 *
 * One org config record holds the default for every table, keyed by target,
 * and the endpoint replaces the whole value. So a write sends the current map
 * back with one entry changed, leaving the other tables' defaults as they were.
 *
 * The id is a hint. The server validates it when written and forgets it when
 * that View is deleted, but a View unshared from the org afterwards stays
 * named. Callers resolve it and fall back rather than trusting it.
 */
export function useOrgDefaultView(target: QueryTarget): OrgDefaultView {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id;

  const setting = useOrgConfig<OrgDefaultViewsConfig>(
    orgId,
    ORG_DEFAULT_VIEWS_KEY,
  );
  const update = useUpdateOrgConfig();

  const current = setting.data ?? EMPTY_ORG_DEFAULT_VIEWS;
  const orgDefaultViewId = current.default_view_id_by_target[target] ?? null;

  const updateAsync = update.mutateAsync;

  const write = React.useCallback(
    async (nextId: string | null) => {
      if (orgId === undefined) {
        throw new Error("Cannot set an org default View with no current org");
      }
      const { [target]: _dropped, ...others } =
        current.default_view_id_by_target;
      const value: OrgDefaultViewsConfig = {
        version: current.version,
        default_view_id_by_target:
          nextId === null ? others : { ...others, [target]: nextId },
      };
      await updateAsync({ orgId, key: ORG_DEFAULT_VIEWS_KEY, value });
    },
    [current, orgId, target, updateAsync],
  );

  const setOrgDefault = React.useCallback(
    (view: ViewRecord) => write(view.view_id),
    [write],
  );

  const clearOrgDefault = React.useCallback(() => write(null), [write]);

  const isOrgDefault = React.useCallback(
    (view: ViewRecord) =>
      orgDefaultViewId !== null && view.view_id === orgDefaultViewId,
    [orgDefaultViewId],
  );

  return {
    orgDefaultViewId,
    isOrgDefault,
    setOrgDefault,
    clearOrgDefault,
    isPending: setting.isPending,
    isSaving: update.isPending,
  };
}
